import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getUsers, getUser, createUser, updateUser,
  updateUserPublicProfile, resetUserPassword, deleteUser
} from '../../services/adminApi';
import { toast } from 'react-toastify';

// ── Create User Modal ──────────────────────────────────────────
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    password: '', confirmPassword: '', dateOfBirth: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await createUser({
        firstName: form.firstName, lastName: form.lastName,
        email: form.email, password: form.password,
        dateOfBirth: form.dateOfBirth || undefined
      });
      toast.success(res.data.message);
      onCreated();
      onClose();
    } catch (err) { setError(err.response?.data?.message || 'Failed to create user'); }
    finally { setLoading(false); }
  };

  const field = (label, key, type = 'text', extra = {}) => (
    <div>
      <label className="form-label">{label}</label>
      <input className="admin-input" type={type} value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} {...extra} />
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>👤 Create User</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {error && <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', color: 'var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13 }}>⚠ {error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {field('First Name *', 'firstName', 'text', { required: true })}
              {field('Last Name *', 'lastName', 'text', { required: true })}
            </div>
            {field('Email *', 'email', 'email', { required: true })}
            {field('Date of Birth', 'dateOfBirth', 'date')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {field('Password *', 'password', 'password', { required: true, minLength: 8, autoComplete: 'new-password' })}
              {field('Confirm Password *', 'confirmPassword', 'password', { required: true, autoComplete: 'new-password' })}
            </div>
            <p style={{ fontSize: 11, color: 'var(--text3)', margin: 0 }}>A QR code will be generated automatically. The user can update their medical info after logging in.</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── User Detail / Edit Modal ───────────────────────────────────
function UserModal({ userId, onClose, onRefresh }) {
  const [user, setUser]       = useState(null);
  const [tab, setTab]         = useState('info');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Editable fields
  const [form, setForm]       = useState({ firstName: '', lastName: '', email: '', dateOfBirth: '', isActive: true });
  const [profile, setProfile] = useState({});
  const [newPw, setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  useEffect(() => {
    if (!userId) return;
    setUser(null); setError('');
    getUser(userId)
      .then(r => {
        const u = r.data.user;
        setUser(u);
        setForm({
          firstName: u.firstName || '', lastName: u.lastName || '',
          email: u.email || '', dateOfBirth: u.dateOfBirth?.slice?.(0, 10) || '',
          isActive: u.isActive !== false
        });
        setProfile(u.publicProfile || {});
      })
      .catch(err => { setError(err.response?.data?.message || 'Failed to load user'); toast.error('Failed to load user'); });
  }, [userId]);

  const handleSaveDetails = async () => {
    setSaving(true); setError('');
    try {
      await updateUser(userId, {
        firstName: form.firstName, lastName: form.lastName,
        email: form.email, dateOfBirth: form.dateOfBirth || undefined,
        isActive: form.isActive
      });
      toast.success('User updated');
      onRefresh();
    } catch (err) { setError(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateUserPublicProfile(userId, profile);
      toast.success('Public profile updated');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    setError('');
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    if (newPw.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await resetUserPassword(userId, newPw);
      toast.success('Password updated');
      setNewPw(''); setConfirmPw('');
    } catch (err) { setError(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Permanently delete this user and all their data? This cannot be undone.')) return;
    try { await deleteUser(userId); toast.success('User deleted'); onRefresh(); onClose(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const toggleField = field => setProfile(p => ({ ...p, [field]: !p[field] }));

  if (!userId) return null;

  const TABS = ['info', 'public profile', 'password', 'danger'];

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <h3>👤 {user ? `${user.firstName} ${user.lastName}` : 'User Details'}</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        {error && !user ? (
          <div className="modal-body">
            <div style={{ color: 'var(--red)', background: 'var(--red-dim)', border: '1px solid #7f1d1d', borderRadius: 'var(--radius)', padding: '14px 16px', fontSize: 13 }}>⚠ {error}</div>
          </div>
        ) : !user ? (
          <div className="modal-body"><div className="empty-state"><div className="spinner" /></div></div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 0, padding: '0 20px', borderBottom: '1px solid var(--border)' }}>
              {TABS.map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '10px 12px',
                  fontSize: 12, fontWeight: 600, textTransform: 'capitalize', fontFamily: 'inherit',
                  color: tab === t ? 'var(--accent)' : 'var(--text3)',
                  borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom: -1
                }}>{t === 'danger' ? '⚠ Danger' : t}</button>
              ))}
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {error && <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', color: 'var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13 }}>⚠ {error}</div>}

              {tab === 'info' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[['First Name', 'firstName'], ['Last Name', 'lastName']].map(([lbl, key]) => (
                      <div key={key}><label className="form-label">{lbl}</label>
                        <input className="admin-input" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} /></div>
                    ))}
                  </div>
                  <div><label className="form-label">Email</label><input className="admin-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
                  <div><label className="form-label">Date of Birth</label><input className="admin-input" type="date" value={form.dateOfBirth} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} /></div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ fontSize: 13 }}>Account Active</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                      style={{ width: 40, height: 22, background: form.isActive ? 'var(--accent)' : 'var(--surface3)', border: 'none', borderRadius: 11, cursor: 'pointer', position: 'relative', padding: 0 }}>
                      <span style={{ position: 'absolute', top: 3, left: form.isActive ? 20 : 3, width: 16, height: 16, background: '#fff', borderRadius: '50%', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, paddingTop: 4, fontSize: 12, color: 'var(--text3)' }}>
                    <span>QR: <span style={{ fontFamily: 'var(--font-mono)' }}>{user.qrCodeId || '—'}</span></span>
                    <span style={{ marginLeft: 'auto' }}>Joined: {new Date(user.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={handleSaveDetails} disabled={saving}>
                      {saving ? <span className="spinner" /> : 'Save Changes'}
                    </button>
                  </div>
                </>
              )}

              {tab === 'public profile' && (
                <>
                  {[['enabled', 'Public profile enabled'], ['showFullName', 'Show full name'],
                    ['showBloodType', 'Show blood type'], ['showAllergies', 'Show allergies'],
                    ['showEmergencyContact', 'Show emergency contact']
                  ].map(([field, label]) => (
                    <div key={field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 13 }}>{label}</span>
                      <button onClick={() => toggleField(field)}
                        className={`btn btn-sm ${profile[field] ? 'btn-success' : 'btn-secondary'}`}>
                        {profile[field] ? 'On' : 'Off'}
                      </button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
                    <button className="btn btn-primary" onClick={handleSaveProfile} disabled={saving}>
                      {saving ? <span className="spinner" /> : 'Save Profile'}
                    </button>
                  </div>
                </>
              )}

              {tab === 'password' && (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>Set a new password for this user's account.</p>
                  <div><label className="form-label">New Password</label><input className="admin-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" /></div>
                  <div><label className="form-label">Confirm Password</label><input className="admin-input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} autoComplete="new-password" /></div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={handleResetPassword} disabled={saving || !newPw}>
                      {saving ? <span className="spinner" /> : 'Update Password'}
                    </button>
                  </div>
                </>
              )}

              {tab === 'danger' && (
                <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', borderRadius: 'var(--radius)', padding: 14 }}>
                  <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 6, fontSize: 13 }}>Delete Account</div>
                  <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
                    Permanently removes this user, their profile, and invalidates all linked QR codes. This cannot be undone.
                  </p>
                  <button className="btn btn-sm btn-danger" onClick={handleDelete}>🗑 Delete User</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────
export default function AdminUsers() {
  const [users, setUsers]     = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getUsers({ page, limit: 20, search: search || undefined })
      .then(r => { setUsers(r.data.users); setPagination(r.data.pagination); })
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <AdminLayout title="Users">
      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="admin-input" placeholder="Search name or email…" value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="toolbar-right">
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{pagination.total ?? '—'} users</span>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Create User</button>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead><tr><th>Name</th><th>Email</th><th>Status</th><th>Public</th><th>Assets</th><th>Joined</th><th></th></tr></thead>
          <tbody>
            {loading ? Array.from({ length: 8 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 7 }).map((_, j) => <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>)}</tr>
            )) : users.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><div className="empty-icon">👤</div><p>No users found</p></div></td></tr>
            ) : users.map(u => (
              <tr key={u._id} style={{ opacity: u.isActive === false ? .5 : 1 }}>
                <td style={{ fontWeight: 600 }}>{u.firstName} {u.lastName}</td>
                <td style={{ color: 'var(--text2)', fontSize: 12 }}>{u.email}</td>
                <td><span className={`badge ${u.isActive !== false ? 'badge-green' : 'badge-red'}`}>{u.isActive !== false ? 'Active' : 'Disabled'}</span></td>
                <td><span className={`badge ${u.publicProfile?.enabled ? 'badge-green' : 'badge-grey'}`}>{u.publicProfile?.enabled ? 'Public' : 'Private'}</span></td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{u.assets?.length ?? 0}</td>
                <td style={{ color: 'var(--text3)', fontSize: 12 }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td><button className="btn btn-sm btn-secondary" onClick={() => setSelectedId(u._id)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>

        {pagination.pages > 1 && (
          <div className="pagination">
            <span className="page-info">Page {pagination.page} of {pagination.pages}</span>
            <div className="page-btns">
              <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: Math.min(pagination.pages, 5) }).map((_, i) => {
                const p = i + 1;
                return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
              })}
              <button className="page-btn" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {selectedId && <UserModal userId={selectedId} onClose={() => { setSelectedId(null); load(); }} onRefresh={load} />}
    </AdminLayout>
  );
}
